/**
 * Low-poly city builder with procedural buildings, trees, and urban scale.
 * Highway corridor: asphalt, shoulders, guardrails; all scrolling scenery spawns
 * outside the guard line (see constants.js).
 */

import * as THREE from "three";

import {
  BUILDING_MAX_ABS_X,
  BUILDING_MIN_ABS_X,
  GUARD_LINE_ABS_X,
  LANE_DIVIDER_X,
  LANE_EDGE_X,
  PROP_MAX_ABS_X,
  PROP_MIN_ABS_X,
  ROAD_SURFACE_HALF,
  SCENERY_KIND_BUILDING,
  SCENERY_KIND_PROP,
  SHOULDER_WIDTH,
  randomSignedAbsX
} from "./constants.js";

/**
 * Posts + continuous beam for one roadside; placed at {@link GUARD_LINE_ABS_X} on the given side.
 *
 * @param {THREE.Scene} scene
 * @param {ReturnType<import("./materials.js").createRaceMaterials>} materials
 * @param {1|-1} sideSign — +1 right, −1 left (from driver’s forward +Z view).
 * @param {number} roadCenterZ — highway strip anchor in world Z.
 * @param {number} roadLen — strip length along Z (matches asphalt).
 */
function addGuardRailSide(scene, materials, sideSign, roadCenterZ, roadLen) {
  const group = new THREE.Group();
  group.position.set(0, 0, roadCenterZ);
  const xBase = GUARD_LINE_ABS_X * sideSign;
  const postMat = materials.rail;
  const halfLen = roadLen * 0.5;
  const spacing = 3.85;

  for (let z = -halfLen + spacing * 0.5; z < halfLen; z += spacing) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.74, 0.22), postMat);
    post.position.set(xBase, 0.39, z);
    /** No shadow maps — projected shadow on the scrolling deck read as rails “creeping” while driving. */
    post.castShadow = false;
    post.receiveShadow = false;
    group.add(post);
  }

  const beam = new THREE.Mesh(
    new THREE.BoxGeometry(0.16, 0.24, roadLen * 0.96),
    postMat
  );
  beam.position.set(xBase + sideSign * 0.06, 0.66, 0);
  beam.castShadow = false;
  beam.receiveShadow = false;
  group.add(beam);

  scene.add(group);
}

/**
 * Repeating dash quads along −Z for lane paint; meshes are appended to `roadLines` for scroll sync.
 *
 * @param {THREE.Scene} scene
 * @param {THREE.Mesh[]} roadLines
 * @param {number} x
 * @param {THREE.Material} material
 * @param {THREE.BufferGeometry} geom — shared plane (width × dash length).
 * @param {number} y
 * @param {number} zStart
 * @param {number} rowSpacing
 * @param {number} rowCount
 */
function addLaneDashColumn(scene, roadLines, x, material, geom, y, zStart, rowSpacing, rowCount) {
  for (let i = 0; i < rowCount; i++) {
    const line = new THREE.Mesh(geom, material);
    line.rotation.x = -Math.PI / 2;
    line.position.set(x, y, zStart - i * rowSpacing);
    scene.add(line);
    roadLines.push(line);
  }
}

/**
 * Low-poly block + pyramid roof + façade windows for roadside city props.
 *
 * @param {ReturnType<import("./materials.js").createRaceMaterials>} materials
 * @param {number} height
 * @param {number} width — extent along local X (faces street).
 * @param {number} depth — extent along local Z.
 */
function createLowPolyBuilding(materials, height, width, depth) {
  const group = new THREE.Group();

  const buildingColors = [materials.blue, materials.yellow, materials.black, materials.rail];
  const bodyMat = buildingColors[Math.floor(Math.random() * buildingColors.length)];

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(width, height, depth),
    bodyMat
  );
  body.position.y = height / 2;
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  const roofHeight = height * (0.15 + Math.random() * 0.1);
  const roofColors = [materials.palm, materials.rail, materials.trunk];
  const roofMat = roofColors[Math.floor(Math.random() * roofColors.length)];

  const roof = new THREE.Mesh(
    new THREE.ConeGeometry(Math.max(width, depth) * 0.65, roofHeight, 4),
    roofMat
  );
  roof.position.y = height + roofHeight * 0.5;
  roof.rotation.y = Math.random() * Math.PI;
  roof.castShadow = true;
  group.add(roof);

  for (let row = 0; row < Math.floor(height / 1.8); row++) {
    for (let col = 0; col < Math.floor(width / 1.4); col++) {
      if (Math.random() > 0.15) {
        const window = new THREE.Mesh(
          new THREE.PlaneGeometry(0.6, 0.6),
          materials.deck
        );
        window.position.set(
          col * 1.4 - width / 2 + 0.7,
          1.2 + row * 1.8,
          depth / 2 + 0.05
        );
        window.castShadow = false;
        group.add(window);
      }
    }
  }

  return group;
}

/**
 * @param {THREE.Scene} scene
 * @param {ReturnType<import("./materials.js").createRaceMaterials>} materials
 * @returns {{ roadLines: THREE.Mesh[], scenery: THREE.Group[], sun: THREE.DirectionalLight }}
 */
export function buildEndlessRaceWorld(scene, materials) {
  const mutedSky = 0xa8bdb2;
  scene.background = new THREE.Color(mutedSky);
  scene.fog = new THREE.Fog(mutedSky, 45, 160);

  const roadLines = [];
  const scenery = [];

  scene.add(new THREE.HemisphereLight(0xe8f5ff, mutedSky, 0.78));
  const sun = new THREE.DirectionalLight(0xffffff, 2.25);
  sun.position.set(-12, 28, 8);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.bias = -0.0003;
  sun.shadow.normalBias = 0.04;
  sun.shadow.camera.left = -50;
  sun.shadow.camera.right = 50;
  sun.shadow.camera.top = 50;
  sun.shadow.camera.bottom = -50;
  scene.add(sun);

  const roadCenterZ = -40;
  const roadLen = 280;

  const deckMat = materials.deck;
  const deck = new THREE.Mesh(new THREE.PlaneGeometry(140, roadLen), deckMat);
  deck.rotation.x = -Math.PI / 2;
  deck.position.set(0, 0, roadCenterZ);
  deck.receiveShadow = true;
  scene.add(deck);

  const roadMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a });
  const road = new THREE.Mesh(
    new THREE.PlaneGeometry(ROAD_SURFACE_HALF * 2, roadLen),
    roadMat
  );
  road.rotation.x = -Math.PI / 2;
  road.position.set(0, 0.01, roadCenterZ);
  road.receiveShadow = true;
  scene.add(road);

  if (SHOULDER_WIDTH > 1e-4) {
    const shoulderMat = materials.shoulder;
    const shoulderY = 0.015;
    const shoulderXCenter = ROAD_SURFACE_HALF + SHOULDER_WIDTH / 2;

    const leftShoulder = new THREE.Mesh(
      new THREE.PlaneGeometry(SHOULDER_WIDTH, roadLen),
      shoulderMat
    );
    leftShoulder.rotation.x = -Math.PI / 2;
    leftShoulder.position.set(-shoulderXCenter, shoulderY, roadCenterZ);
    leftShoulder.receiveShadow = true;
    scene.add(leftShoulder);

    const rightShoulder = new THREE.Mesh(
      new THREE.PlaneGeometry(SHOULDER_WIDTH, roadLen),
      shoulderMat
    );
    rightShoulder.rotation.x = -Math.PI / 2;
    rightShoulder.position.set(shoulderXCenter, shoulderY, roadCenterZ);
    rightShoulder.receiveShadow = true;
    scene.add(rightShoulder);
  }

  addGuardRailSide(scene, materials, -1, roadCenterZ, roadLen);
  addGuardRailSide(scene, materials, 1, roadCenterZ, roadLen);

  const yMark = 0.021;
  const zStart = 24;
  const zStep = 5.2;
  const nDash = 70;
  const dashLen = 3.8;
  const geoDivider = new THREE.PlaneGeometry(0.22, dashLen);
  const geoEdge = new THREE.PlaneGeometry(0.16, dashLen * 1.05);
  const centerLineMat = materials.white;

  /** Side inter-lane dashes only; center stripe is one scrolling solid (reference: solid white center). */
  const sideDividerXs = LANE_DIVIDER_X.filter((x) => Math.abs(x) > 1e-6);
  for (const xDiv of sideDividerXs) {
    addLaneDashColumn(
      scene,
      roadLines,
      xDiv,
      centerLineMat,
      geoDivider,
      yMark,
      zStart,
      zStep,
      nDash
    );
  }

  const centerStrip = new THREE.Mesh(
    new THREE.PlaneGeometry(0.14, roadLen * 0.965),
    centerLineMat.clone()
  );
  centerStrip.rotation.x = -Math.PI / 2;
  centerStrip.position.set(0, yMark + 0.002, roadCenterZ);
  scene.add(centerStrip);
  roadLines.push(centerStrip);
  for (const xEdge of LANE_EDGE_X) {
    addLaneDashColumn(scene, roadLines, xEdge, materials.white, geoEdge, yMark, zStart, zStep, nDash);
  }

  for (let i = 0; i < 48; i++) {
    const z = 26 - i * 5.8;

    if (i % 2 === 0) {
      const buildingX = randomSignedAbsX(BUILDING_MIN_ABS_X, BUILDING_MAX_ABS_X);

      const heightVar = 3.5 + Math.random() * 6.5;
      const widthVar = 3.2 + Math.random() * 3.8;
      const depthVar = 2.8 + Math.random() * 3.2;

      const building = createLowPolyBuilding(materials, heightVar, widthVar, depthVar);
      building.position.set(buildingX, 0, z);
      building.userData.sceneryKind = SCENERY_KIND_BUILDING;

      scene.add(building);
      scenery.push(building);
    } else {
      const treeType = Math.random();

      if (treeType > 0.6) {
        const treeX = randomSignedAbsX(PROP_MIN_ABS_X, PROP_MAX_ABS_X);

        const trunkMesh = new THREE.Mesh(
          new THREE.CylinderGeometry(0.16, 0.24, 2.2, 8),
          materials.trunk
        );
        trunkMesh.position.y = 1.1;
        trunkMesh.castShadow = true;

        const treeGroup = new THREE.Group();
        treeGroup.position.set(treeX, 0, z);
        treeGroup.userData.sceneryKind = SCENERY_KIND_PROP;
        treeGroup.add(trunkMesh);

        for (let j = 0; j < 6; j++) {
          const leaf = new THREE.Mesh(
            new THREE.ConeGeometry(0.68, 1.5, 4),
            materials.palm
          );
          leaf.position.y = 2.4;
          leaf.rotation.z = (j / 6) * Math.PI * 2;
          leaf.rotation.x = Math.PI / 2.5;
          leaf.castShadow = true;
          treeGroup.add(leaf);
        }

        scene.add(treeGroup);
        scenery.push(treeGroup);
      } else if (treeType > 0.2) {
        const rockX = randomSignedAbsX(PROP_MIN_ABS_X, PROP_MAX_ABS_X);

        const rocks = new THREE.Group();
        rocks.position.set(rockX, 0, z);
        rocks.userData.sceneryKind = SCENERY_KIND_PROP;

        for (let r = 0; r < 3 + Math.floor(Math.random() * 2); r++) {
          const rock = new THREE.Mesh(
            new THREE.DodecahedronGeometry(0.55 + Math.random() * 0.65, 0),
            materials.rail
          );
          rock.position.set(
            (Math.random() - 0.5) * 2.2,
            0.4 + r * 0.5,
            (Math.random() - 0.5) * 1.5
          );
          rock.scale.y = 0.7;
          rock.rotation.set(Math.random(), Math.random(), Math.random());
          rock.castShadow = true;
          rocks.add(rock);
        }

        scene.add(rocks);
        scenery.push(rocks);
      }
    }
  }

  return { roadLines, scenery, sun };
}
